// import components
import { Typography } from '@mui/material';
import { Icon } from '@sdb/planning-ui';
import {
  StyledBoxFlexAlignCenterSpaceBetween,
  StyledBoxFlexColumnSmallGap
} from 'components/shared/Box/Box.styles';
import NoteForm from './components/NoteForm';
import NoteList from './components/NoteList';
import { StyledButtonIcon, StyledNoteContainerBox } from './Notes.styles';

// import hooks
import { useState } from 'react';
import useGetEmployeeNotes from './hooks/useGetEmployeeNotes';
import useGetRequestNotes from 'components/flex-portal/FlexHistory/hooks/useGetRequestNotes';

// import types
import type { DutyLineNote } from 'RestAPIs/employees/hours-table/types';
import type { NotesProps } from './Notes.types';
import { NotesTypeEnums } from './Notes.types';

// import utils
import { IconContainer } from 'components/shared/Header/Header.styles';
import { t } from 'i18next';
import { NOTE_VARIANT } from './Notes.consts';

type NotesByType = Record<
  NotesTypeEnums,
  | ReturnType<typeof useGetEmployeeNotes>['data']
  | ReturnType<typeof useGetRequestNotes>['data']
  | DutyLineNote[]
>;

export default function Notes({
  employeeId,
  type = NotesTypeEnums.EMPLOYEE_NOTE,
  noteData = null,
  dutyLineId,
  requestId,
  isEmployeePortal = false,
  isSavedByParent = false,
  onAddNote,
  onRemoveNote,
  size = 'default',
  noteVariant = NOTE_VARIANT.DEFAULT,
  showTooltip = false,
  isWebSocketEnabled = false
}: NotesProps) {
  'use memo';

  // only fetch employee notes if type is EMPLOYEE_NOTE
  const { data: employeeNotes = [], isFetching } = useGetEmployeeNotes(
    { employeeId: employeeId as number },
    { enabled: !!employeeId && type === NotesTypeEnums.EMPLOYEE_NOTE }
  );

  const { data: requestNoteData = [], isFetching: isFetchingRequestNotes } =
    useGetRequestNotes(
      { requestId },
      {
        enabled: !!requestId && type === NotesTypeEnums.FLEX_MATCH_REQUEST
      }
    );

  const [showNoteForm, setShowNoteForm] = useState(false);
  const handleToggleForm = () => setShowNoteForm((visible) => !visible);

  // Note: we filter out private notes in the employee portal in the FE, this should be handled in the BE ideally
  const visibleNoteData =
    type === NotesTypeEnums.DUTY_LINE
      ? (noteData ?? []).filter(
          (note): note is DutyLineNote =>
            'id' in note &&
            (!isEmployeePortal || !note.flags?.includes('PRIVATE'))
        )
      : [];

  const notesByType: NotesByType = {
    [NotesTypeEnums.EMPLOYEE_NOTE]: employeeNotes,
    [NotesTypeEnums.FLEX_MATCH_REQUEST]: requestNoteData,
    [NotesTypeEnums.DUTY_LINE]: visibleNoteData
  };

  const notes = notesByType[type];
  const isCompact = size === 'compact';

  return (
    <StyledNoteContainerBox>
      <StyledBoxFlexColumnSmallGap>
        <StyledBoxFlexAlignCenterSpaceBetween isCompact={isCompact}>
          <Typography
            variant={isCompact ? 'body2' : 'h6'}
            component={isCompact ? 'p' : 'h3'}
            color={isCompact ? 'text.secondary' : 'text.primary'}
          >
            {t('notes')} ({notes?.length})
          </Typography>
          {!isEmployeePortal && (
            <StyledButtonIcon
              onClick={handleToggleForm}
              color="primary"
              variant={isCompact ? 'text' : 'outlined'}
              size={isCompact ? 'small' : 'medium'}
              isCompact={isCompact}
            >
              {showNoteForm ? (
                t('cancel')
              ) : (
                <>
                  <IconContainer iconSize={isCompact ? 'SMALL' : 'MEDIUM'}>
                    <Icon type="plus" fontSize="inherit" color="primary" />
                  </IconContainer>
                  {t('addNote')}
                </>
              )}
            </StyledButtonIcon>
          )}
        </StyledBoxFlexAlignCenterSpaceBetween>
        {showNoteForm && !isEmployeePortal && (
          <NoteForm
            employeeId={employeeId}
            setShowNoteForm={setShowNoteForm}
            dutyLineId={dutyLineId}
            type={type}
            isEmployeePortal={isEmployeePortal}
            isSavedByParent={isSavedByParent}
            onAddNote={onAddNote}
            noteVariant={noteVariant}
            isCompact={isCompact}
            requestId={requestId}
            isWebSocketEnabled={isWebSocketEnabled}
          />
        )}
      </StyledBoxFlexColumnSmallGap>
      {notes && (
        <NoteList
          notes={notes}
          type={type}
          isEmployeePortal={isEmployeePortal}
          isFetching={
            type === NotesTypeEnums.EMPLOYEE_NOTE ||
            type === NotesTypeEnums.FLEX_MATCH_REQUEST
              ? isFetching || isFetchingRequestNotes
              : false
          }
          isSavedByParent={isSavedByParent}
          onRemoveNote={onRemoveNote}
          noteVariant={noteVariant}
          isCompact={isCompact}
          showTooltip={showTooltip}
          isWebSocketEnabled={isWebSocketEnabled}
        />
      )}
    </StyledNoteContainerBox>
  );
}
